// TODO:  https://cncenter.atlassian.net/browse/FEG-1287

import * as Sentry from '@sentry/browser';
import addScriptToDOM from '@utils/addScript';
import videoAdRequest from '../components/gjvp/videoAdRequest';
import videoEvents from '../components/gjvp/videoEvents';
import { GjvpPlayerConfigType, GjvpVideosType, GjvpPlayerType } from '../types/gjvpPlayer';

type PlayerFunction = (elementId: string) => GjvpPlayerType;

// FN pro nacteni reklamni url
// Dostupné i při 0 videích na stránce - využívají je i jiné služby (např. přehrávač Tivio)
const videoAdReq = videoAdRequest();
window.getSmallPlayerTagUrl = videoAdReq.getSmallPlayerTagUrl.bind(videoAdReq.getSmallPlayerTagUrl);
window.getBigPlayerTagUrl = videoAdReq.getBigPlayerTagUrl.bind(videoAdReq.getBigPlayerTagUrl);

const waitForPlayerFunction = (
  getter: () => PlayerFunction | undefined,
  maxAttempts = 60,
  interval = 100
): Promise<PlayerFunction> => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const check = () => {
      const result = getter();
      if (result) {
        resolve(result);
        return;
      }

      attempts++;
      if (attempts >= maxAttempts) {
        const err = new Error('VP Player function did not appear in time');
        Sentry.captureException(err);
        reject(err);
        return;
      }

      setTimeout(check, interval);
    };

    check();
  });
};

const waitForPlayer = (templateLocation: string): Promise<PlayerFunction> => {
  return waitForPlayerFunction(() => {
    const isVertical = templateLocation === 'vertical' || templateLocation === 'verticalCarousel';

    return isVertical ? window.vpVerticalPlayer : window.vpPlayer;
  });
};

// ========================================================================
// INIT CODE
// ========================================================================

const initializeVideos = (): void => {
  const gjvpVideos = (window.gjvpVideos || []) as GjvpVideosType[];

  if (gjvpVideos.length === 0) {
    return;
  }
  // gPlayer gemius
  addScriptToDOM({ src: 'https://spir.hit.gemius.pl/gplayer.js' }).catch((err: Error) => {
    console.error(err.message);
  });

  // Mapování: srcScriptVPlayer -> pole videoItemů, které ho potřebují
  const scriptsMap = new Map<string, GjvpVideosType[]>();

  gjvpVideos.forEach((videoItem) => {
    let srcScriptVPlayer: string;
    if (videoItem.templateLocation === 'audio') {
      srcScriptVPlayer = `https://host.vpplayer.tech/audio-player/${videoItem.playerId}/${videoItem.videoId}.js?v=1`;
    } else if (
      videoItem.templateLocation === 'vertical' ||
      videoItem.templateLocation === 'verticalCarousel'
    ) {
      srcScriptVPlayer = `https://host.vpplayer.tech/vertical-player/${videoItem.playerId}.js?v=2`;
    } else {
      srcScriptVPlayer = `https://host.vpplayer.tech/player/${videoItem.playerProjectId || 'v2.0.3'}/vpplayer.js?v=1.2`;
    }
    if (!scriptsMap.has(srcScriptVPlayer)) {
      scriptsMap.set(srcScriptVPlayer, []);
    }

    const items = scriptsMap.get(srcScriptVPlayer);
    if (items) {
      items.push(videoItem);
    }
  });

  // Načti všechny scripty
  const scriptPromises = Array.from(scriptsMap.keys()).map((src) => addScriptToDOM({ src }));

  Promise.all(scriptPromises)
    .then(() => {
      const initialized = new Set<string>();

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const item = gjvpVideos.find((v) => v.elementId === entry.target.id);
            if (!item || initialized.has(item.elementId)) return;

            initialized.add(item.elementId);

            const typePlayer =
              item.templateLocation === 'audio'
                ? 'audio-player'
                : item.templateLocation === 'vertical' ||
                    item.templateLocation === 'verticalCarousel'
                  ? 'vertical-player'
                  : 'player';

            const API_URL = `https://host.vpplayer.tech/${typePlayer}`;
            let fetchUrl = `${API_URL}/${item.playerId}/${item.videoId}`;
            fetchUrl += item.playerProjectId === 'agmipnzu' ? '/smyrfcjo.json' : '.json';

            fetch(fetchUrl)
              .then((response) => response.json())
              .then((data: GjvpPlayerConfigType) => {
                // Počkat až bude player k dispozici
                return waitForPlayer(item.templateLocation || '').then((playerFn) => {
                  const vpPlayer = playerFn(item.elementId);

                  if (item.disableAdsForPremiumUsers) {
                    data.video.advertising = false;
                  }

                  if (item.isVideoLockedForUser) {
                    data.video.videoLocking.isEnabled = true;
                    data.video.videoLocking.value = item.videoLockedTimeNumber;
                  }

                  if (item.videoLockedTimeNumber === 0 && item.isVideoLockedForUser) {
                    const overlayElement = document.getElementById(`overlay-${item.elementId}`);
                    if (overlayElement) {
                      overlayElement.style.visibility = 'visible';
                    }
                  }

                  vpPlayer.setup(data);
                  videoEvents({
                    videoVpPlayer: vpPlayer,
                    categoryName: item.categoryName || '',
                    templateLocation: item.templateLocation || '',
                    videoPlayerId: item.playerId,
                    gemiusIdentifier: item.gemiusIdentifier || '',
                    typology: item.typology || '',
                  });
                });
              })
              .catch((err) => {
                Sentry.captureException(err);
              });

            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.5 }
      );

      // Observe video elements
      const observeVideos = (): void => {
        gjvpVideos.forEach((item) => {
          const el = document.getElementById(item.elementId);
          if (el && !initialized.has(item.elementId)) {
            observer.observe(el);
          }
        });
      };

      observeVideos();

      const mutationObserver = new MutationObserver(() => observeVideos());
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    })
    .catch((err) => {
      Sentry.captureException(err);
    });
};

// Inicializace na DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVideos);
} else {
  initializeVideos();
}
